import React, { useState, useEffect } from 'react'
import { Input, CompanyPicker, Select, formatSelectOptions } from './'
import { personService } from '../services'
import { useGlobalState } from '../hooks'

const val = (e) => (e.target !== undefined ? e.target.value : e.value)

// Shared create/edit form for a network person — used both by the Nätverk
// person page (network/hooks/useNetworkDialog.js) and by PersonPicker's
// "+ Skapa ... som ny person" create flow, so there is exactly one person
// form in the app. `person` prefills values AND switches the submit to an
// update; pass `initialName` instead when you only want to prefill a fresh
// create (e.g. from a picker's search term).
//
// Registers its submit handler on `dialogRef.current.submit` so the modal's
// footer "Spara"/"Skapa" button (owned by useModalDialog, outside this
// component's state) can trigger it — see the CLAUDE.md gotcha on
// cross-closure modal submit.
export const PersonEditForm = ({ person, initialName, dialogRef, hideModalDialog, setLoadingModalDialog, onSaved }) => {
  const [state, setState] = useState({
    person_name: person?.person_name || initialName || '',
    person_email: person?.person_email || '',
    person_linkedin: person?.person_linkedin || '',
    person_phone: person?.person_phone || '',
    person_title: person?.person_title || '',
    person_notes: person?.person_notes || '',
    company_uuid: person?.company_id ? person.company_uuid : '',
  })
  const [error, setError] = useState(null)
  const [duplicate, setDuplicate] = useState(null)

  // Kayenta-kontakt is a create-only field here: on an existing person the contacts are
  // managed on their Nätverk detail page (add/remove hit their own endpoints), and this
  // form's PUT deliberately doesn't touch them.
  const [loggedInUser] = useGlobalState('loggedInUser')
  const [users, setUsers] = useState([])
  const [contactUserIds, setContactUserIds] = useState([])

  useEffect(() => {
    if (person) return
    const ctrl = new AbortController()
    personService.getUsers(ctrl.signal)
      .then(res => setUsers(res.users || []))
      .catch(() => {})
    return () => ctrl.abort()
  }, [person])

  // Prefill with the logged-in user, so the common case ("this is my contact") is zero
  // extra clicks. Waits for both the user list and loggedInUser to resolve, and only
  // seeds an empty selection so it never fights a choice the user already made.
  useEffect(() => {
    if (person || !loggedInUser?.user_id || !users.length) return
    setContactUserIds(prev => {
      if (prev.length) return prev
      return users.some(u => u.user_id === loggedInUser.user_id) ? [loggedInUser.user_id] : prev
    })
  }, [person, loggedInUser, users])

  const toggleContact = (userId) => {
    setContactUserIds(prev => prev.some(id => String(id) === String(userId))
      ? prev.filter(id => String(id) !== String(userId))
      : [...prev, userId])
  }

  const set = (key, v) => setState(prev => ({ ...prev, [key]: v }))

  const submit = () => {
    setError(null)
    setDuplicate(null)

    if (!state.person_name) {
      setError('Namn krävs')
      return Promise.resolve()
    }
    if (!state.person_email && !state.person_linkedin) {
      setError('E-post eller LinkedIn krävs')
      return Promise.resolve()
    }
    if (!person && !contactUserIds.length) {
      setError('Minst en Kayenta-kontakt krävs')
      return Promise.resolve()
    }

    setLoadingModalDialog(true)
    const call = person
      ? personService.update(person.person_uuid, state)
      : personService.create({ ...state, kayenta_contact_user_ids: contactUserIds })

    return call
      .then(res => {
        setLoadingModalDialog(false)
        hideModalDialog()
        onSaved && onSaved(res.person)
      })
      .catch(err => {
        setLoadingModalDialog(false)
        if (err && typeof err === 'object' && 'existingPerson' in err) {
          setDuplicate(err)
        } else {
          setError(err.message || 'Kunde inte spara person')
        }
      })
  }

  if (dialogRef) dialogRef.current = { submit }

  return (
    <div className="network-form">
      {error && <div className="form-error">{error}</div>}
      {duplicate &&
        <div className="form-error-callout">
          <p>{duplicate.message}</p>
          <p>Sök och välj den befintliga personen i stället.</p>
        </div>
      }
      <Input name="person_name" label="Namn" required value={state.person_name} onChange={e => set('person_name', val(e))} />
      <div className="form-row">
        <Input name="person_email" label="E-post" value={state.person_email} onChange={e => set('person_email', val(e))} />
        <Input name="person_linkedin" label="LinkedIn" placeholder="länk eller vanity-namn" value={state.person_linkedin} onChange={e => set('person_linkedin', val(e))} />
      </div>
      <p className="form-hint">Minst en av e-post och LinkedIn krävs — de är nätverkets dubblettnycklar.</p>
      <div className="form-row">
        <CompanyPicker
          name="company_uuid"
          label="Företag"
          value={state.company_uuid}
          onChange={({ value: v }) => set('company_uuid', v)}
          allowCreate
          isNullable
          disabled={!!person?.is_internal}
        />
        <Input name="person_title" label="Titel" value={state.person_title} onChange={e => set('person_title', val(e))} disabled={!!person?.is_internal} />
      </div>
      <Input name="person_phone" label="Telefon" value={state.person_phone} onChange={e => set('person_phone', val(e))} />
      {!person &&
        <>
          <Select
            name="kayenta_contact_user_ids"
            /* Select has no `required` prop (Input does) — but it renders whatever `label`
               is inside a <span>, so passing an element reuses Input's exact asterisk. */
            label={<>Kayenta-kontakt <span className="label-required">*</span></>}
            showMultiLabel
            isMulti
            placeholder="Välj Kayenta-person"
            value={contactUserIds.map(id => ({ value: id }))}
            options={formatSelectOptions({ options: users, optionValue: 'user_id', optionLabel: 'name', tooltip: 'email' })}
            onChange={e => toggleContact(e.target !== undefined ? e.target.value : e.value)}
          />
          <p className="form-hint">Vem på Kayenta känner personen? Förifylld med dig. Flera tillåtna.</p>
        </>
      }
      <Input name="person_notes" label="Anteckningar" value={state.person_notes} onChange={e => set('person_notes', val(e))} />
    </div>
  )
}
